import type {
  AnalysisFamily,
  EvidenceRecord,
  FindingAgeBand,
  FindingBusinessTier,
  FindingEnrichmentState,
  FindingFilters,
  FindingPrivateReadState,
  FindingQueryState,
  FindingRemediationState,
  FindingSlaState,
  FindingSortKey,
  FindingStatus,
  FindingViewPreset,
  Severity,
  SortDirection,
  ValidationStage,
} from "../../api/types.js";

export const SEVERITIES = [
  "critical",
  "high",
  "medium",
  "low",
] as const satisfies readonly Severity[];
export const FINDING_STATUSES = [
  "candidate",
  "confirmed",
  "needs_human",
  "refuted",
  "suppressed",
] as const satisfies readonly FindingStatus[];
export const ACTIONABLE_STATUSES = [
  "confirmed",
  "needs_human",
] as const satisfies readonly FindingStatus[];
export const VALIDATION_DEPTHS = [1, 2, 3, 4] as const satisfies readonly ValidationStage[];
export const REPLAY_STATUSES = [
  "unverified",
  "matched",
  "mismatch",
  "unavailable",
] as const satisfies readonly EvidenceRecord["replayStatus"][];
export const BUSINESS_TIERS = [
  "tier_0",
  "tier_1",
  "tier_2",
  "tier_3",
  "unknown",
] as const satisfies readonly FindingBusinessTier[];
export const ENRICHMENT_STATES = [
  "not_started",
  "complete",
  "stale",
  "unavailable",
] as const satisfies readonly FindingEnrichmentState[];
export const REMEDIATION_STATES = [
  "not_started",
  "draft",
  "previewed",
  "generating",
  "verifying",
  "ready",
  "approved",
  "rejected",
  "rolled_back",
  "expired",
] as const satisfies readonly FindingRemediationState[];
export const PRIVATE_READ_STATES = [
  "read",
  "unread",
  "not_set",
] as const satisfies readonly FindingPrivateReadState[];
export const AGE_BANDS = [
  "fresh_0_7d",
  "aging_8_30d",
  "stale_31_90d",
  "legacy_91d_plus",
] as const satisfies readonly FindingAgeBand[];
export const SLA_STATES = [
  "overdue",
  "due_soon",
  "on_track",
  "unassigned",
] as const satisfies readonly FindingSlaState[];

export const ANALYSIS_FAMILIES = [
  "sast",
  "secrets",
  "sca",
  "iac",
  "container",
  "dast",
  "pqc",
  "remediation",
  "runtime_defense_feedback",
] as const satisfies readonly AnalysisFamily[];

export const ANALYSIS_FAMILY_LABELS: Record<AnalysisFamily, string> = {
  sast: "SAST",
  secrets: "Secrets",
  sca: "SCA",
  iac: "IaC",
  container: "Container",
  dast: "DAST",
  pqc: "PQC Readiness",
  remediation: "Remediation",
  runtime_defense_feedback: "Runtime feedback",
};

export const ANALYSIS_FAMILY_GROUPS = [
  { id: "code", label: "Code and packages", families: ["sast", "secrets", "sca"] },
  { id: "infrastructure", label: "Infrastructure", families: ["iac", "container"] },
  { id: "runtime", label: "Runtime", families: ["dast", "runtime_defense_feedback"] },
  { id: "readiness", label: "Readiness", families: ["pqc", "remediation"] },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  families: readonly AnalysisFamily[];
}>;

export const DEFAULT_FINDING_QUERY: FindingQueryState = {
  query: "",
  severities: [],
  statuses: [...ACTIONABLE_STATUSES],
  validationDepths: [],
  repositoryIds: [],
  businessTiers: [],
  componentKeys: [],
  engineIds: [],
  enrichmentStates: [],
  remediationStates: [],
  privateReadStates: [],
  privateCategoryKeys: [],
  ageBands: [],
  assigneeIds: [],
  slaStates: [],
  ruleIds: [],
  cwes: [],
  owaspCategories: [],
  languages: [],
  analysisFamilies: [],
  replayStatuses: [],
  minConfidence: 0,
  page: 1,
  pageSize: 50,
  preset: "actionable",
  sort: "default",
  direction: "desc",
};

function csvValues<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
): T[] {
  const raw = params.get(key);
  if (!raw) return [];
  const requested = new Set(raw.split(","));
  return allowed.filter((value) => requested.has(value));
}

/** Parse a bookmarkable findings URL using allowlists for every enum value. */
export function findingQueryFromSearch(search: string): FindingQueryState {
  const params = new URLSearchParams(search);
  const presetValue = params.get("preset");
  const preset: FindingViewPreset =
    presetValue === "all" || presetValue === "custom" || presetValue === "actionable"
      ? presetValue
      : "actionable";
  const statuses = csvValues(params, "statuses", FINDING_STATUSES);
  const depths = (params.get("validationDepths") ?? "")
    .split(",")
    .map(Number)
    .filter((value): value is ValidationStage =>
      VALIDATION_DEPTHS.includes(value as ValidationStage),
    );
  const sortValue = params.get("sortBy");
  const sorts: readonly FindingSortKey[] = [
    "default",
    "severity",
    "status",
    "validationDepth",
    "confidence",
    "path",
    "updatedAt",
  ];
  const pageSizeValue = Number(params.get("pageSize"));
  const pageValue = Number(params.get("page"));
  const confidenceValue = Number(params.get("confidence"));
  return {
    ...DEFAULT_FINDING_QUERY,
    query: params.get("query") ?? "",
    severities: csvValues(params, "severities", SEVERITIES),
    statuses: params.has("statuses") ? statuses : preset === "all" ? [] : [...ACTIONABLE_STATUSES],
    validationDepths: depths,
    repositoryIds: (params.get("repositoryIds") ?? "").split(",").filter(Boolean),
    businessTiers: csvValues(params, "businessTiers", BUSINESS_TIERS),
    componentKeys: (params.get("componentKeys") ?? "").split(",").filter(Boolean),
    engineIds: (params.get("engineIds") ?? "").split(",").filter(Boolean),
    enrichmentStates: csvValues(params, "enrichmentStates", ENRICHMENT_STATES),
    remediationStates: csvValues(params, "remediationStates", REMEDIATION_STATES),
    privateReadStates: csvValues(params, "privateReadStates", PRIVATE_READ_STATES),
    privateCategoryKeys: (params.get("privateCategoryKeys") ?? "").split(",").filter(Boolean),
    ageBands: csvValues(params, "ageBands", AGE_BANDS),
    assigneeIds: (params.get("assigneeIds") ?? "").split(",").filter(Boolean),
    slaStates: csvValues(params, "slaStates", SLA_STATES),
    ruleIds: (params.get("ruleIds") ?? "").split(",").filter(Boolean),
    cwes: (params.get("cwes") ?? "").split(",").filter(Boolean),
    owaspCategories: (params.get("owaspCategories") ?? "").split(",").filter(Boolean),
    languages: (params.get("languages") ?? "").split(",").filter(Boolean),
    analysisFamilies: csvValues(params, "analysisFamilies", ANALYSIS_FAMILIES),
    replayStatuses: csvValues(params, "replayStatuses", REPLAY_STATUSES),
    minConfidence:
      Number.isFinite(confidenceValue) && confidenceValue >= 0 && confidenceValue <= 100
        ? confidenceValue
        : 0,
    page: Number.isInteger(pageValue) && pageValue > 0 ? pageValue : 1,
    pageSize: [25, 50, 100].includes(pageSizeValue) ? pageSizeValue : 50,
    preset,
    sort:
      typeof sortValue === "string" && sorts.includes(sortValue as FindingSortKey)
        ? (sortValue as FindingSortKey)
        : "default",
    direction: params.get("sortDirection") === "asc" ? "asc" : ("desc" as SortDirection),
    scanId: params.get("scanId") || undefined,
  };
}

/** Stable query serialization makes filtered views bookmarkable and shareable. */
export function findingQueryToSearch(state: FindingQueryState): string {
  const params = new URLSearchParams();
  if (state.query.trim()) params.set("query", state.query.trim());
  if (state.severities.length) params.set("severities", state.severities.join(","));
  params.set("statuses", state.statuses.join(","));
  if (state.validationDepths.length)
    params.set("validationDepths", state.validationDepths.join(","));
  if (state.repositoryIds.length) params.set("repositoryIds", state.repositoryIds.join(","));
  if (state.businessTiers.length) params.set("businessTiers", state.businessTiers.join(","));
  if (state.componentKeys.length) params.set("componentKeys", state.componentKeys.join(","));
  if (state.engineIds.length) params.set("engineIds", state.engineIds.join(","));
  if (state.enrichmentStates.length)
    params.set("enrichmentStates", state.enrichmentStates.join(","));
  if (state.remediationStates.length)
    params.set("remediationStates", state.remediationStates.join(","));
  if (state.privateReadStates.length)
    params.set("privateReadStates", state.privateReadStates.join(","));
  if (state.privateCategoryKeys.length)
    params.set("privateCategoryKeys", state.privateCategoryKeys.join(","));
  if (state.ageBands.length) params.set("ageBands", state.ageBands.join(","));
  if (state.assigneeIds.length) params.set("assigneeIds", state.assigneeIds.join(","));
  if (state.slaStates.length) params.set("slaStates", state.slaStates.join(","));
  if (state.ruleIds.length) params.set("ruleIds", state.ruleIds.join(","));
  if (state.cwes.length) params.set("cwes", state.cwes.join(","));
  if (state.owaspCategories.length) params.set("owaspCategories", state.owaspCategories.join(","));
  if (state.languages.length) params.set("languages", state.languages.join(","));
  if (state.analysisFamilies.length)
    params.set("analysisFamilies", state.analysisFamilies.join(","));
  if (state.replayStatuses.length) params.set("replayStatuses", state.replayStatuses.join(","));
  if (state.minConfidence > 0) params.set("confidence", String(state.minConfidence));
  params.set("preset", state.preset);
  params.set("sortBy", state.sort);
  params.set("sortDirection", state.direction);
  params.set("page", String(state.page));
  params.set("pageSize", String(state.pageSize));
  if (state.scanId) params.set("scanId", state.scanId);
  return `?${params.toString()}`;
}

export function statusesForPreset(preset: Exclude<FindingViewPreset, "custom">): FindingStatus[] {
  return preset === "actionable" ? [...ACTIONABLE_STATUSES] : [];
}

export function presetForStatuses(statuses: readonly FindingStatus[]): FindingViewPreset {
  if (statuses.length === 0) return "all";
  if (
    statuses.length === ACTIONABLE_STATUSES.length &&
    ACTIONABLE_STATUSES.every((status) => statuses.includes(status))
  ) {
    return "actionable";
  }
  return "custom";
}

export function toFindingFilters(state: FindingQueryState): FindingFilters {
  const filters: FindingFilters = {
    page: state.page,
    pageSize: state.pageSize,
  };
  const query = state.query.trim();
  if (query) filters.query = query;
  if (state.severities.length > 0) filters.severities = state.severities;
  if (state.statuses.length > 0) filters.statuses = state.statuses;
  if (state.validationDepths.length > 0) filters.validationDepths = state.validationDepths;
  if (state.repositoryIds.length > 0) filters.repositoryIds = state.repositoryIds;
  if (state.businessTiers.length > 0) filters.businessTiers = state.businessTiers;
  if (state.componentKeys.length > 0) filters.componentKeys = state.componentKeys;
  if (state.engineIds.length > 0) filters.engineIds = state.engineIds;
  if (state.enrichmentStates.length > 0) filters.enrichmentStates = state.enrichmentStates;
  if (state.remediationStates.length > 0) filters.remediationStates = state.remediationStates;
  if (state.privateReadStates.length > 0) filters.privateReadStates = state.privateReadStates;
  if (state.privateCategoryKeys.length > 0) filters.privateCategoryKeys = state.privateCategoryKeys;
  if (state.ageBands.length > 0) filters.ageBands = state.ageBands;
  if (state.assigneeIds.length > 0) filters.assigneeIds = state.assigneeIds;
  if (state.slaStates.length > 0) filters.slaStates = state.slaStates;
  if (state.ruleIds.length > 0) filters.ruleIds = state.ruleIds;
  if (state.cwes.length > 0) filters.cwes = state.cwes;
  if (state.owaspCategories.length > 0) filters.owaspCategories = state.owaspCategories;
  if (state.languages.length > 0) filters.languages = state.languages;
  if (state.analysisFamilies.length > 0) {
    filters.analysisFamilies = ANALYSIS_FAMILIES.filter((family) =>
      state.analysisFamilies.includes(family),
    );
  }
  if (state.replayStatuses.length > 0) filters.replayStatuses = state.replayStatuses;
  if (state.minConfidence > 0) filters.minConfidence = state.minConfidence / 100;
  if (state.scanId) filters.scanId = state.scanId;
  filters.sortBy = state.sort;
  filters.sortDirection = state.direction;
  return filters;
}

export function filtersForExport(
  state: FindingQueryState,
): Omit<FindingFilters, "page" | "pageSize"> {
  const filters = toFindingFilters(state);
  const exported: Omit<FindingFilters, "page" | "pageSize"> = {};
  if (filters.query) exported.query = filters.query;
  if (filters.severities) exported.severities = filters.severities;
  if (filters.statuses) exported.statuses = filters.statuses;
  if (filters.validationDepths) exported.validationDepths = filters.validationDepths;
  if (filters.repositoryIds) exported.repositoryIds = filters.repositoryIds;
  if (filters.businessTiers) exported.businessTiers = filters.businessTiers;
  if (filters.componentKeys) exported.componentKeys = filters.componentKeys;
  if (filters.engineIds) exported.engineIds = filters.engineIds;
  if (filters.enrichmentStates) exported.enrichmentStates = filters.enrichmentStates;
  if (filters.remediationStates) exported.remediationStates = filters.remediationStates;
  if (filters.privateReadStates) exported.privateReadStates = filters.privateReadStates;
  if (filters.privateCategoryKeys) exported.privateCategoryKeys = filters.privateCategoryKeys;
  if (filters.ageBands) exported.ageBands = filters.ageBands;
  if (filters.assigneeIds) exported.assigneeIds = filters.assigneeIds;
  if (filters.slaStates) exported.slaStates = filters.slaStates;
  if (filters.ruleIds) exported.ruleIds = filters.ruleIds;
  if (filters.cwes) exported.cwes = filters.cwes;
  if (filters.owaspCategories) exported.owaspCategories = filters.owaspCategories;
  if (filters.languages) exported.languages = filters.languages;
  if (filters.analysisFamilies) exported.analysisFamilies = filters.analysisFamilies;
  if (filters.replayStatuses) exported.replayStatuses = filters.replayStatuses;
  if (filters.minConfidence !== undefined) exported.minConfidence = filters.minConfidence;
  if (filters.scanId) exported.scanId = filters.scanId;
  if (filters.sortBy) exported.sortBy = filters.sortBy;
  if (filters.sortDirection) exported.sortDirection = filters.sortDirection;
  return exported;
}

export function appendFindingFilters(params: URLSearchParams, filters: FindingFilters): void {
  if (filters.query) params.set("query", filters.query);
  if (filters.severities?.length) params.set("severities", filters.severities.join(","));
  if (filters.statuses?.length) params.set("statuses", filters.statuses.join(","));
  if (filters.validationDepths?.length) {
    params.set("validationDepths", filters.validationDepths.join(","));
  }
  if (filters.repositoryIds?.length) params.set("repositoryIds", filters.repositoryIds.join(","));
  if (filters.businessTiers?.length) params.set("businessTiers", filters.businessTiers.join(","));
  if (filters.componentKeys?.length) params.set("componentKeys", filters.componentKeys.join(","));
  if (filters.engineIds?.length) params.set("engineIds", filters.engineIds.join(","));
  if (filters.enrichmentStates?.length) {
    params.set("enrichmentStates", filters.enrichmentStates.join(","));
  }
  if (filters.remediationStates?.length) {
    params.set("remediationStates", filters.remediationStates.join(","));
  }
  if (filters.privateReadStates?.length) {
    params.set("privateReadStates", filters.privateReadStates.join(","));
  }
  if (filters.privateCategoryKeys?.length) {
    params.set("privateCategoryKeys", filters.privateCategoryKeys.join(","));
  }
  if (filters.ageBands?.length) params.set("ageBands", filters.ageBands.join(","));
  if (filters.assigneeIds?.length) params.set("assigneeIds", filters.assigneeIds.join(","));
  if (filters.slaStates?.length) params.set("slaStates", filters.slaStates.join(","));
  if (filters.findingIds?.length) params.set("findingIds", filters.findingIds.join(","));
  if (filters.ruleIds?.length) params.set("ruleIds", filters.ruleIds.join(","));
  if (filters.cwes?.length) params.set("cwes", filters.cwes.join(","));
  if (filters.owaspCategories?.length) {
    params.set("owaspCategories", filters.owaspCategories.join(","));
  }
  if (filters.languages?.length) params.set("languages", filters.languages.join(","));
  if (filters.analysisFamilies?.length) {
    params.set("analysisFamilies", filters.analysisFamilies.join(","));
  }
  if (filters.replayStatuses?.length) {
    params.set("replayStatuses", filters.replayStatuses.join(","));
  }
  if (filters.minConfidence !== undefined) {
    params.set("minConfidence", filters.minConfidence.toFixed(2));
  }
  if (filters.scanId) params.set("scanId", filters.scanId);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDirection) params.set("sortDirection", filters.sortDirection);
  params.set("page", String(filters.page));
  params.set("pageSize", String(filters.pageSize));
}

export function countAdvancedFilters(state: FindingQueryState): number {
  return (
    state.validationDepths.length +
    state.repositoryIds.length +
    state.businessTiers.length +
    state.componentKeys.length +
    state.engineIds.length +
    state.enrichmentStates.length +
    state.remediationStates.length +
    state.privateReadStates.length +
    state.privateCategoryKeys.length +
    state.ageBands.length +
    state.assigneeIds.length +
    state.slaStates.length +
    state.ruleIds.length +
    state.cwes.length +
    state.owaspCategories.length +
    state.languages.length +
    state.replayStatuses.length +
    (state.minConfidence > 0 ? 1 : 0)
  );
}

export function hasAnyFilter(state: FindingQueryState): boolean {
  return (
    state.query.trim().length > 0 ||
    state.severities.length > 0 ||
    state.analysisFamilies.length > 0 ||
    state.preset !== "actionable" ||
    countAdvancedFilters(state) > 0
  );
}

export function toggleOrderedValue<T>(selected: readonly T[], value: T, order: readonly T[]): T[] {
  const next = selected.includes(value)
    ? selected.filter((item) => item !== value)
    : [...selected, value];
  return order.filter((item) => next.includes(item));
}
